import React, { useState, useEffect } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography, Collapse, Chip, IconButton } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

const ProjectTOC = ({ steps, currentStep, onStepPress, isOpen }) => {
  const [openItems, setOpenItems] = useState({});

  // Function to get all parent stepIds for a given stepId
  const getParentStepIds = (stepId) => {
    const stepIdStr = stepId?.toString() || '';
    const parts = stepIdStr.split('_');
    const parentIds = [];
    for (let i = 1; i < parts.length; i++) {
      parentIds.push(parts.slice(0, i).join('_'));
    }
    return parentIds;
  };

  // Effect to automatically expand to current step
  useEffect(() => {
    if (currentStep) {
      const parentIds = getParentStepIds(currentStep);
      setOpenItems((prev) => {
        const newOpenItems = { ...prev };
        parentIds.forEach((id) => {
          newOpenItems[id] = true;
        });
        return newOpenItems;
      });
    }
  }, [currentStep]);

  const toggleItem = (stepId, event) => {
    event.stopPropagation(); // Prevent the click from bubbling up
    setOpenItems((prev) => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  const renderSteps = (steps, depth = '') =>
    steps?.map((step, i) => {
      const stepId = depth ? `${depth}_${i}` : `${i}`;
      const hasNestedSteps = step.steps && step.steps.length > 0;
      const isOpen = openItems[stepId];
      const isCurrent = currentStep === stepId;

      return (
        <Box key={i}>
          <ListItem 
            disablePadding
            secondaryAction={
              hasNestedSteps && (
                <IconButton 
                  edge="end" 
                  onClick={(e) => toggleItem(stepId, e)}
                  size="small"
                >
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )
            }
          >
            <ListItemButton
              onClick={() => onStepPress(stepId)}
              sx={{
                pl: depth ? depth.toString().split('_').length * 2 : 1,
                backgroundColor: isCurrent ? 'primary.light' : 'transparent',
                '&:hover': {
                  backgroundColor: isCurrent ? 'primary.light' : 'grey.100'
                }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{step.title || step.name}</Typography>
                    {hasNestedSteps && (
                      <Chip
                        size="small"
                        label={step.steps.length}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
          {hasNestedSteps && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderSteps(step.steps, stepId)}
              </List>
            </Collapse>
          )}
        </Box>
      );
    });

  return (
    <Box
      sx={{
        width: 200,
        minWidth: 200,
        maxWidth: 200,
        borderRight: '1px solid',
        borderColor: 'divider',
        display: isOpen ? 'block' : 'none',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      <List
        sx={{
          height: '100%',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        {renderSteps(steps)}
      </List>
    </Box>
  );
};

export default ProjectTOC;
